import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { auth, getFns } from '../firebase'
import { useRole } from './roles'
import { setBlackboxOperator } from './blackbox'

/**
 * Auth v2 (RECYCLE_PLAN.md): the shared shop login stays signed in, and each
 * person unlocks the portal with an individual operator PIN. The PIN is
 * verified SERVER-SIDE (operatorPinVerify callable); this context only holds
 * the verified operator session and relocks after idle time.
 */
const STORAGE_KEY = 'artgold.operator.session.v1'
const IDLE_RELOCK_MS = 10 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 30 * 1000
const ACTIVITY_EVENTS = ['pointerdown', 'keydown']

// Roles that unlock with a PIN. Admin (master emails) bypasses the lock.
const STAFF_ROLES = ['staff', 'office', 'manager']

const OperatorContext = createContext(null)

/**
 * Clears the persisted operator session synchronously. Sign-out handlers MUST
 * call this before signOut(): the auth-state change unmounts OperatorProvider
 * in the same render pass, so its cleanup effect cannot be relied on — a stale
 * session would otherwise leak to the next account on a shared device.
 */
export function clearOperatorSession() {
  try { window.sessionStorage.removeItem(STORAGE_KEY) } catch {}
  setBlackboxOperator(null)
}

function readStoredOperator() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data?.operatorId || !data?.name || !data?.uid) return null
    // The session is bound to the Firebase account it was verified under.
    const currentUid = auth.currentUser?.uid || null
    if (currentUid && data.uid !== currentUid) return null
    return data
  } catch {
    return null
  }
}

export function OperatorProvider({ children }) {
  const { role, user, loading } = useRole()
  const [operator, setOperator] = useState(() => readStoredOperator())
  const lastActivityRef = useRef(Date.now())

  const requiresPin = false // Individual account login authorizes navigation; AU actions still call checkPin.

  const lock = useCallback(() => {
    setOperator(null)
    try { window.sessionStorage.removeItem(STORAGE_KEY) } catch {}
  }, [])

  // Side-effect-free verification: resolves the operator behind a PIN without
  // touching the portal session. Used by per-action gates (e.g. transaction
  // start/confirm) so a second person's PIN doesn't hijack the global identity
  // that stamps unrelated audit events.
  const checkPin = useCallback(async (pin) => {
    const call = httpsCallable(getFns(), 'operatorPinVerify')
    const { data } = await call({ pin })
    return { operatorId: data.operatorId, name: data.name }
  }, [])

  const verifyPin = useCallback(async (pin) => {
    const { operatorId, name } = await checkPin(pin)
    const next = {
      operatorId,
      name,
      uid: auth.currentUser?.uid || null,
      verifiedAt: Date.now(),
    }
    setOperator(next)
    lastActivityRef.current = Date.now()
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {}
    return next
  }, [checkPin])

  // Auto-relock after idle time while an operator session is active.
  useEffect(() => {
    if (!requiresPin || !operator) return undefined

    const onActivity = () => { lastActivityRef.current = Date.now() }
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }))

    const interval = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_RELOCK_MS) lock()
    }, IDLE_CHECK_INTERVAL_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity))
      window.clearInterval(interval)
    }
  }, [requiresPin, operator, lock])

  // Signing out clears the operator session; an account switch on a shared
  // device invalidates a session verified under a different uid.
  useEffect(() => {
    if (!loading && !user) { lock(); return }
    if (user && operator?.uid && operator.uid !== user.uid) lock()
  }, [loading, user, operator, lock])

  // Keep client logging stamped with the current operator.
  useEffect(() => {
    setBlackboxOperator(operator?.name || null)
  }, [operator])

  const value = useMemo(() => ({
    operator,
    requiresPin,
    isLocked: requiresPin && !operator,
    verifyPin,
    checkPin,
    lock,
  }), [operator, requiresPin, verifyPin, checkPin, lock])

  return <OperatorContext.Provider value={value}>{children}</OperatorContext.Provider>
}

export function useOperator() {
  const ctx = useContext(OperatorContext)
  return ctx || { operator: null, requiresPin: false, isLocked: false, verifyPin: async () => null, checkPin: async () => null, lock: () => {} }
}
